export type TemplateHeaderFormat = 'NONE' | 'TEXT' | 'IMAGE' | 'VIDEO' | 'DOCUMENT';
export type TemplateButtonType = 'QUICK_REPLY' | 'URL' | 'PHONE_NUMBER';
export type TemplateVariableKind = 'common' | 'lead';

export interface TemplateButtonDraft {
  type: TemplateButtonType;
  text: string;
  url?: string;
  phoneNumber?: string;
}

export interface TemplateVariableDraft {
  index: number;
  title: string;
  example: string;
  kind: TemplateVariableKind;
  source?: string | null;
}

export interface OfficialTemplateDraft {
  name: string;
  metaTemplateName: string;
  category: string;
  language: string;
  body: string;
  footer?: string;
  headerFormat: TemplateHeaderFormat;
  headerText?: string;
  mediaId?: string;
  buttons: TemplateButtonDraft[];
  variables: TemplateVariableDraft[];
  entrypointId?: string;
  editing?: boolean;
}

function placeholders(text: string): RegExpExecArray[] {
  const pattern = /\{\{(\d+)\}\}/g;
  const found: RegExpExecArray[] = [];
  let match: RegExpExecArray | null = pattern.exec(text);
  while (match) {
    found.push(match);
    match = pattern.exec(text);
  }
  return found;
}

export function placeholderIndexes(...parts: Array<string | null | undefined>): number[] {
  const set = new Set<number>();
  for (const part of parts) {
    if (!part) continue;
    for (const match of placeholders(part)) {
      const n = Number(match[1]);
      if (n > 0) set.add(n);
    }
  }
  const list: number[] = [];
  set.forEach((n) => list.push(n));
  list.sort((a, b) => a - b);
  return list;
}

export function syncVariableDrafts(
  indexes: number[],
  current: TemplateVariableDraft[],
): TemplateVariableDraft[] {
  return indexes.map((index) => {
    const existing = current.find((v) => v.index === index);
    return existing ?? { index, title: '', example: '', kind: 'common', source: null };
  });
}

export function nextVariableIndex(text: string): number {
  const indexes = placeholderIndexes(text);
  return indexes.length ? Math.max(...indexes) + 1 : 1;
}

export function validateOfficialTemplate(draft: OfficialTemplateDraft): string[] {
  const errors: string[] = [];
  if (!draft.name.trim()) errors.push('Informe o nome do modelo.');
  if (!draft.editing) {
    if (!draft.entrypointId?.trim()) errors.push('Escolha o número oficial deste modelo.');
    if (!/^[a-z0-9_]+$/.test(draft.metaTemplateName.trim())) {
      errors.push('O nome na Meta só pode ter letras minúsculas, números e _.');
    } else if (draft.metaTemplateName.trim().length > 512) {
      errors.push('O nome na Meta pode ter no máximo 512 caracteres.');
    }
    if (draft.category !== 'MARKETING' && draft.category !== 'UTILITY') {
      errors.push('Escolha Marketing ou Utilidade.');
    }
    if (!draft.language.trim()) errors.push('Escolha um idioma.');
  }

  const body = draft.body ?? '';
  if (!body.trim()) errors.push('O corpo da mensagem é obrigatório.');
  else if (body.trim().length > 1024) errors.push('O corpo pode ter no máximo 1024 caracteres.');

  let headerText: string | null = null;
  if (draft.headerFormat === 'TEXT') {
    headerText = draft.headerText ?? '';
    if (!headerText.trim()) errors.push('Informe o texto do cabeçalho.');
    else if (headerText.trim().length > 60) errors.push('O cabeçalho pode ter no máximo 60 caracteres.');
    if (placeholders(headerText).length > 1) {
      errors.push('A text header accepts at most one variable.');
    }
  } else if (draft.headerFormat === 'IMAGE' || draft.headerFormat === 'VIDEO' || draft.headerFormat === 'DOCUMENT') {
    if (!draft.mediaId?.trim()) errors.push('Envie o arquivo de exemplo do cabeçalho.');
  }

  const footer = draft.footer ?? '';
  if (footer.trim().length > 60) errors.push('O rodapé pode ter no máximo 60 caracteres.');
  if (placeholders(footer).length > 0) errors.push('O rodapé não pode ter variável.');

  edgeErrors('O corpo', body, errors);
  if (headerText != null) edgeErrors('O cabeçalho', headerText, errors);
  if (hasAdjacent(body)) errors.push('As variáveis do corpo não podem ficar juntas. Coloque texto entre elas.');
  if (headerText && hasAdjacent(headerText)) {
    errors.push('As variáveis do cabeçalho não podem ficar juntas. Coloque texto entre elas.');
  }

  if (draft.buttons.length > 10) errors.push('A template accepts at most 10 buttons.');
  let phones = 0;
  let urls = 0;
  const urlTexts: string[] = [];
  for (const button of draft.buttons) {
    if (!button.text.trim()) errors.push('Informe o texto do botão.');
    else if (button.text.trim().length > 25) errors.push('O texto do botão pode ter no máximo 25 caracteres.');
    if (placeholders(button.text).length > 0) errors.push('O texto do botão não pode ter variável.');
    if (button.type === 'PHONE_NUMBER') {
      phones += 1;
      const phone = (button.phoneNumber ?? '').replace(/[\s\-()]/g, '');
      if (!/^\+?[0-9]{8,20}$/.test(phone)) errors.push('Informe um telefone válido com o código do país.');
    } else if (button.type === 'URL') {
      urls += 1;
      const url = (button.url ?? '').trim();
      if (!url) errors.push('Informe o link do botão.');
      else {
        if (url.length > 2000) errors.push('O link do botão pode ter no máximo 2000 caracteres.');
        if (!/^https?:\/\//i.test(url)) errors.push('O link do botão precisa começar com http:// ou https://.');
        const matches = placeholders(url);
        if (matches.length > 1 || (matches.length === 1 && !/\{\{\d+\}\}$/.test(url.trim()))) {
          errors.push('A variável do link precisa ser a única e ficar no fim do endereço.');
        }
        urlTexts.push(url);
      }
    }
  }
  if (phones > 1 || urls > 2) errors.push('Use no máximo 1 botão de telefone e 2 botões de link.');

  const indexes = placeholderIndexes(headerText, body, ...urlTexts);
  const defined = draft.variables.map((v) => v.index);
  if (indexes.length === 0 && draft.variables.length > 0) {
    errors.push('Toda variável definida precisa aparecer no modelo.');
  }
  if (indexes.length > 0) {
    const max = Math.max(...indexes);
    for (let i = 1; i <= max; i += 1) {
      if (!indexes.includes(i)) {
        errors.push('As variáveis devem ser {{1}}, {{2}}, … sem pular números.');
        break;
      }
    }
    if (defined.length !== indexes.length || defined.some((i) => !indexes.includes(i))) {
      errors.push('Cada variável do texto precisa de uma definição, e cada definição precisa aparecer no texto.');
    }
  }
  const seen = new Set<number>();
  for (const variable of draft.variables) {
    if (variable.index < 1 || seen.has(variable.index)) {
      errors.push('As variáveis devem ser {{1}}, {{2}}, … sem pular números.');
    }
    seen.add(variable.index);
    if (!variable.title.trim() || !variable.example.trim()) {
      errors.push('Informe um título e um exemplo para cada variável.');
    } else if (variable.example.trim().length > 15) {
      errors.push('Cada exemplo de variável pode ter no máximo 15 caracteres.');
    }
    if (variable.kind !== 'common' && variable.kind !== 'lead') {
      errors.push('Escolha manual ou nome do contato para cada variável.');
    } else if (variable.kind === 'lead' && variable.source !== 'chat.name') {
      errors.push('Automatic variables use the chat name.');
    }
  }

  return errors.filter((error, index) => errors.indexOf(error) === index);
}

function edgeErrors(label: string, text: string, errors: string[]) {
  const trimmed = text.trim();
  if (!trimmed) return;
  if (/^\{\{\d+\}\}/.test(trimmed)) errors.push(`${label} não pode começar com variável.`);
  if (/\{\{\d+\}\}$/.test(trimmed)) errors.push(`${label} não pode terminar com variável.`);
}

function hasAdjacent(text: string): boolean {
  const matches = placeholders(text);
  for (let i = 0; i < matches.length - 1; i += 1) {
    const current = matches[i];
    const next = matches[i + 1];
    const between = text.slice((current.index ?? 0) + current[0].length, next.index ?? 0);
    if (!between.trim()) return true;
  }
  return false;
}
